import axios from 'axios';

const SAFE_METHODS = new Set(['get', 'head', 'options', 'trace']);

// Read the CSRF token from the vx_csrf cookie (httponly=false, readable by JS)
function readCsrfCookie() {
  const m = document.cookie.match(/(?:^|;\s*)vx_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

// Shared in-flight promise to avoid parallel /csrf-token fetches
let _csrfPending = null;

async function ensureCsrfToken() {
  let token = readCsrfCookie();
  if (token) return token;
  // Cookie absent — fetch it (deduplicates concurrent calls)
  if (!_csrfPending) {
    _csrfPending = axios
      .get('/api/auth/csrf-token', {
        baseURL: process.env.REACT_APP_API_URL || '',
        withCredentials: true,
      })
      .finally(() => { _csrfPending = null; });
  }
  await _csrfPending;
  return readCsrfCookie();
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor: attach CSRF token to all state-changing requests ─────
api.interceptors.request.use(async (config) => {
  if (!SAFE_METHODS.has((config.method || 'get').toLowerCase())) {
    const token = await ensureCsrfToken();
    if (token) config.headers['X-CSRF-Token'] = token;
  }
  return config;
});

// ── Response interceptor ──────────────────────────────────────────────────────
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status  = err.response?.status;
    const detail  = err.response?.data?.detail || '';

    // CSRF token stale or missing — refresh and retry once
    if (status === 403 && detail.includes('CSRF') && !err.config._csrfRetried) {
      err.config._csrfRetried = true;
      _csrfPending = null; // force re-fetch
      await axios.get('/api/auth/csrf-token', {
        baseURL: process.env.REACT_APP_API_URL || '',
        withCredentials: true,
      });
      const token = readCsrfCookie();
      if (token) err.config.headers = { ...err.config.headers, 'X-CSRF-Token': token };
      return axios(err.config);
    }

    // 401 — redirect to login (except on auth pages or /me checks)
    const isAuthCheck = err.config?.url?.includes('/api/auth/me');
    const onAuthPage  = ['/login', '/register', '/forgot-password', '/reset-password'].some(
      p => window.location.pathname.startsWith(p)
    );
    if (status === 401 && !onAuthPage && !isAuthCheck) {
      localStorage.removeItem('zenkart_token');
      localStorage.removeItem('zenkart_user');
      window.location.href = '/login';
    }

    return Promise.reject(err);
  }
);

export default api;
